import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatTL, formatDateTR, todayISO } from "@/lib/format";
import { PageHeader, AddButton, Field, inputCls, StatusPill } from "@/components/shared";
import { usePermission } from "@/lib/permissions";
import FilterBar from "@/components/FilterBar";
import { downloadCSV, fmtDate, fmtNum } from "@/lib/csv";
import { Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: { label: "Bekliyor", cls: "bg-[#D4A373]/20 text-[#8A5A44]" },
  collected: { label: "Tahsil edildi", cls: "bg-[#4A6741]/15 text-[#4A6741]" },
  overdue: { label: "Vadesi geçti", cls: "bg-[#B24C4C]/15 text-[#B24C4C]" },
};

const emptyForm = { customer: "", amount: "", due_date: todayISO(), status: "pending", note: "" };

export default function Receivables() {
  const canEdit = usePermission("receivables", "edit");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/receivables");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((r) => {
      if (q && !`${r.customer} ${r.note || ""}`.toLowerCase().includes(q)) return false;
      if (status && r.status !== status) return false;
      if (dateFrom && r.due_date < dateFrom) return false;
      if (dateTo && r.due_date > dateTo) return false;
      return true;
    });
  }, [items, search, status, dateFrom, dateTo]);

  const exportCSV = () => {
    downloadCSV(filtered, [
      { label: "Müşteri", key: "customer" },
      { label: "Vade", get: (r) => fmtDate(r.due_date) },
      { label: "Tutar (TL)", get: (r) => fmtNum(r.amount) },
      { label: "Durum", get: (r) => STATUS[r.status]?.label || r.status },
      { label: "Not", key: "note" },
    ], `alacaklar_${todayISO()}.csv`);
  };

  const clearFilter = () => { setSearch(""); setStatus(""); setDateFrom(""); setDateTo(""); };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, amount: String(r.amount) }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) || 0 };
    try {
      if (editing) { await api.put(`/receivables/${editing.id}`, payload); toast.success("Alacak güncellendi"); }
      else { await api.post("/receivables", payload); toast.success("Alacak eklendi"); }
      setOpen(false); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu alacağı silmek istediğinize emin misiniz?")) return;
    await api.delete(`/receivables/${id}`);
    toast.success("Silindi"); load();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 fade-in max-w-[1400px]">
      <PageHeader
        eyebrow="Müşteri alacakları"
        title="Alacaklar"
        description="Müşterilerinizden bekleyen tahsilatları yönetin."
        action={canEdit ? <AddButton onClick={openAdd} label="Yeni Alacak" testid="add-receivable-btn" /> : null}
      />

      <FilterBar
        search={search} onSearch={setSearch}
        status={status} onStatus={setStatus}
        statusOptions={[
          { value: "pending", label: "Bekliyor" },
          { value: "collected", label: "Tahsil edildi" },
          { value: "overdue", label: "Vadesi geçti" },
        ]}
        dateFrom={dateFrom} onDateFrom={setDateFrom}
        dateTo={dateTo} onDateTo={setDateTo}
        onExport={exportCSV}
        onClear={clearFilter}
        searchPlaceholder="Müşteri, not…"
        testidPrefix="receivables-filter"
      />

      <div className="bg-white border border-[#E6E4DD] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="receivables-table">
            <thead>
              <tr className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C5B57] border-b border-[#E6E4DD]">
                <th className="px-6 py-4">Müşteri</th>
                <th className="px-6 py-4">Vade</th>
                <th className="px-6 py-4">Tutar</th>
                <th className="px-6 py-4">Durum</th>
                <th className="px-6 py-4">Not</th>
                <th className="px-6 py-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="6" className="px-6 py-12 text-center text-[#5C5B57] text-sm">Henüz alacak eklenmedi.</td></tr>}
              {items.length > 0 && filtered.length === 0 && <tr><td colSpan="6" className="px-6 py-12 text-center text-[#5C5B57] text-sm">Filtreye uyan kayıt bulunamadı.</td></tr>}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-[#E6E4DD]/60 hover:bg-[#F0EFEA]/40 transition-colors" data-testid={`receivable-row-${r.id}`}>
                  <td className="px-6 py-4 text-[#2D2C2A] font-medium">{r.customer}</td>
                  <td className="px-6 py-4 text-[#5C5B57]">{formatDateTR(r.due_date)}</td>
                  <td className="px-6 py-4 font-mono font-medium text-[#2D2C2A]">{formatTL(r.amount)}</td>
                  <td className="px-6 py-4"><StatusPill status={r.status} map={STATUS} /></td>
                  <td className="px-6 py-4 text-[#5C5B57] text-sm max-w-xs truncate">{r.note || "-"}</td>
                  <td className="px-6 py-4 text-right">
                    {canEdit && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEdit(r)} data-testid={`edit-receivable-${r.id}`} className="p-2 rounded-lg hover:bg-[#F0EFEA] text-[#5C5B57]"><Pencil size={16} /></button>
                      <button onClick={() => remove(r.id)} data-testid={`delete-receivable-${r.id}`} className="p-2 rounded-lg hover:bg-[#B24C4C]/10 text-[#B24C4C]"><Trash2 size={16} /></button>
                    </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="receivable-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl text-[#2D2C2A]">{editing ? "Alacak Düzenle" : "Yeni Alacak"}</h3>
              <button onClick={() => setOpen(false)} className="text-[#5C5B57]"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Müşteri"><input required className={inputCls} data-testid="receivable-customer-input" value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tutar (₺)"><input required type="number" step="0.01" min="0" className={inputCls} data-testid="receivable-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
                <Field label="Vade Tarihi"><input required type="date" className={inputCls} data-testid="receivable-due-date-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
              </div>
              <Field label="Durum">
                <select className={inputCls} data-testid="receivable-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="pending">Bekliyor</option>
                  <option value="collected">Tahsil edildi</option>
                  <option value="overdue">Vadesi geçti</option>
                </select>
              </Field>
              <Field label="Not"><textarea className={inputCls} rows={3} data-testid="receivable-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-[#E6E4DD] rounded-xl px-6 py-3 hover:bg-[#F0EFEA]">İptal</button>
                <button type="submit" data-testid="receivable-submit" className="flex-1 bg-[#8A5A44] text-white hover:bg-[#714835] rounded-xl px-6 py-3 font-medium">{editing ? "Güncelle" : "Ekle"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
